import type { TWidgetPackageJson } from '@lucimark/shared/models/widgetPackageJson'
import { buildWidgetBundleFileMap, getTarFirstHeaders, getWidgetPackageJsonFromTarFiles, parseTar } from '@lucimark/shared/helpers/widgetBundle'
import { decompressSync } from 'fflate'
import { sha256 } from 'js-sha256'
import { isE2ePlaybackProbeEnabled } from '@/helpers/e2ePlaybackProbe'
import { isAndroidWebView } from '@/helpers/platform'

/**
 * Production: injectManifest emits `sw-widget.js` at the app base.
 * Dev: vite-plugin-pwa serves the same logical worker as `dev-sw.js?dev-sw` (module); `/sw-widget.js` is not a real file and would return SPA HTML.
 */
function widgetServiceWorkerScriptUrl(): string {
  const rel = import.meta.env.DEV ? 'dev-sw.js?dev-sw' : 'sw-widget.js'
  return new URL(rel, `${location.origin}${import.meta.env.BASE_URL}`).href
}

function widgetServiceWorkerRegisterOptions(): RegistrationOptions {
  const scope = new URL(import.meta.env.BASE_URL, location.origin).pathname || '/'
  return import.meta.env.DEV
    ? { scope, type: 'module' }
    : { scope }
}

const WIDGET_PATH_PREFIX = '/w/'

let swRegistration: ServiceWorkerRegistration | null = null

/** Return the widget Service Worker registration so callers can postMessage (e.g. PRECACHE_URLS) to it even when it is not the page controller. */
export function getWidgetServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return swRegistration
}

/** Wait up to this long for the SW to control the page before mounting the app. */
const CONTROLLER_WAIT_MS = 8000

/** Service Workers require a secure context (HTTPS or localhost). On Android, file/custom schemes don't support SW. */
function canRegisterServiceWorker(): boolean {
  if (typeof location === 'undefined') { return false }
  if (location.protocol !== 'http:' && location.protocol !== 'https:') { return false }
  return typeof window !== 'undefined' && window.isSecureContext !== false
}

/**
 * True when the app is loaded via Tauri's asset protocol (e.g. Android staging/production with
 * WebviewUrl::App). In that case origin is tauri.localhost and there is no HTTP server to fetch
 * sw-widget.js from, so we skip SW registration and use index.html fallback.
 */
function isTauriAssetOrigin(): boolean {
  if (typeof location === 'undefined') { return false }
  return location.hostname === 'tauri.localhost'
}

function formatSwRegistrationError(e: unknown): string {
  if (e instanceof DOMException) {
    return `DOMException ${e.name} (${e.code}): ${e.message}`
  }
  if (e instanceof Error) {
    return `${e.name}: ${e.message}${e.stack ? `\n${e.stack}` : ''}`
  }
  return String(e)
}

/**
 * Register the widget Service Worker (call once at app init).
 * Waits for the SW to control the page so iframe navigations to /w/* are intercepted.
 * Resolves when controller is set or after CONTROLLER_WAIT_MS (then mount anyway).
 * On Android WebView, SW may fail to register (e.g. script fetch); widgets still work via index.html fallback.
 */
export async function registerWidgetServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // Playwright route mocks are bypassed when a SW controls fetch; E2E playback tests skip registration.
  if (isE2ePlaybackProbeEnabled()) {
    return null
  }
  if (!('serviceWorker' in navigator)) {
    if (isAndroidWebView()) {
      const origin = typeof location !== 'undefined' ? location.origin : ''
      console.warn(
        '[widgets] (Android) Service Worker API not available. Browsers only expose it in a secure context (HTTPS or localhost).',
        'You are likely on http://<LAN-IP>:5173, which is not secure. Widgets still work via index.html fallback; for full SPA widgets in dev use an HTTPS dev server (e.g. Vite server.https).',
        { origin }
      )
    }
    return null
  }
  if (!canRegisterServiceWorker()) {
    const proto = typeof location !== 'undefined' ? location.protocol : 'unknown'
    console.warn('[widgets] Service Worker requires http(s); current protocol:', proto, isAndroidWebView() ? '(Android)' : '')
    return null
  }
  // Android staging/production: app is loaded via WebviewUrl::App (tauri.localhost). No HTTP server
  // serves sw-widget.js there; skip registration and use index.html fallback (avoids "Failed to fetch script").
  if (isAndroidWebView() && isTauriAssetOrigin()) {
    return null
  }
  const swScriptUrl = widgetServiceWorkerScriptUrl()
  try {
    swRegistration = await navigator.serviceWorker.register(swScriptUrl, widgetServiceWorkerRegisterOptions())
    await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<void>((resolve) => {
        setTimeout(resolve, CONTROLLER_WAIT_MS)
      })
    ])
    if (!navigator.serviceWorker.controller) {
      let onController: () => void
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          navigator.serviceWorker.removeEventListener('controllerchange', onController)
          resolve()
        }, CONTROLLER_WAIT_MS)
        onController = () => {
          clearTimeout(timeout)
          navigator.serviceWorker.removeEventListener('controllerchange', onController)
          resolve()
        }
        navigator.serviceWorker.addEventListener('controllerchange', onController)
        if (navigator.serviceWorker.controller) {
          clearTimeout(timeout)
          navigator.serviceWorker.removeEventListener('controllerchange', onController)
          resolve()
        }
      })
    }
    return swRegistration
  }
  catch (e: unknown) {
    const msg = formatSwRegistrationError(e)
    console.warn('[widgets] Service Worker registration failed:', msg, isAndroidWebView() ? '(Android — widgets will use index.html fallback)' : '')
    return null
  }
}

function pathIdForUrn(urn: string): string {
  return sha256(urn).substring(0, 16)
}

/**
 * Extract .tgz blob to a virtual file map (path -> { bytes, mime }) with the given prefix.
 * Throws if the blob is not valid gzip or the tar contains no regular files; logs first headers when empty for debugging.
 */
function extractTgzToFileMap(tgzBlob: Blob, scopePrefix: string): Promise<Record<string, { bytes: Uint8Array, mime: string }>> {
  return tgzBlob.arrayBuffer().then((buffer) => {
    const decompressed = decompressSync(new Uint8Array(buffer))
    const tarFiles = parseTar(decompressed)
    if (Object.keys(tarFiles).length === 0) {
      const firstHeaders = getTarFirstHeaders(decompressed, 5)
      console.warn(
        '[widgets] Widget .tgz has no regular files. Decompressed length:',
        decompressed.length,
        'First headers (name, typeFlag decimal, size):',
        firstHeaders.map(h => ({ name: h.name, typeFlag: h.typeFlag, size: h.size }))
      )
      throw new Error(
        'Widget bundle is empty or invalid: the .tgz contains no regular files. '
        + 'Ensure the asset is the actual widget .tgz (application/gzip). '
        + 'Check the console for first-header details (typeFlag 0/48/55 = regular file).'
      )
    }
    return buildWidgetBundleFileMap(tarFiles, scopePrefix)
  })
}

const SW_NOT_READY_MSG = 'No active service worker'

/** True only when the Service Worker API exists (e.g. false in some Android WebViews or insecure contexts). */
function hasServiceWorkerAPI(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

/**
 * Wait for the Service Worker to control the page. Resolves when controller is set or after timeoutMs.
 */
function waitForController(timeoutMs: number = 1500): Promise<void> {
  if (!hasServiceWorkerAPI()) { return Promise.resolve() }
  if (navigator.serviceWorker.controller) { return Promise.resolve() }
  return new Promise((resolve) => {
    let onController: () => void
    const timeout = setTimeout(() => {
      navigator.serviceWorker.removeEventListener('controllerchange', onController)
      resolve()
    }, timeoutMs)
    onController = () => {
      clearTimeout(timeout)
      navigator.serviceWorker.removeEventListener('controllerchange', onController)
      resolve()
    }
    navigator.serviceWorker.addEventListener('controllerchange', onController)
    if (navigator.serviceWorker.controller) {
      clearTimeout(timeout)
      navigator.serviceWorker.removeEventListener('controllerchange', onController)
      resolve()
    }
  })
}

/**
 * Send file map to the Service Worker for the given prefix.
 */
function sendFilesToSw(prefix: string, files: Record<string, { bytes: Uint8Array, mime: string }>): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!hasServiceWorkerAPI()) {
      reject(new Error('Service worker not available'))
      return
    }
    const sw = navigator.serviceWorker.controller
    if (!sw) {
      reject(new Error(SW_NOT_READY_MSG))
      return
    }
    const channel = new MessageChannel()
    channel.port1.onmessage = (e: MessageEvent) => {
      if ((e.data as { type?: string })?.type === 'MOUNT_READY') { resolve() }
      else { reject(new Error('Unexpected SW response')) }
    }
    sw.postMessage({ type: 'MOUNT_FILES', payload: { prefix, files } }, [channel.port2])
  })
}

const MOUNT_RETRY_ATTEMPTS = 3
const MOUNT_RETRY_DELAY_MS = 600

async function sendFilesToSwWithRetry(prefix: string, files: Record<string, { bytes: Uint8Array, mime: string }>): Promise<void> {
  let lastErr: Error | null = null
  for (let attempt = 0; attempt <= MOUNT_RETRY_ATTEMPTS; attempt++) {
    try {
      await sendFilesToSw(prefix, files)
      return
    }
    catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e))
      if (lastErr.message !== SW_NOT_READY_MSG || attempt === MOUNT_RETRY_ATTEMPTS) {
        throw lastErr
      }
      await waitForController(2500)
      await new Promise(r => setTimeout(r, MOUNT_RETRY_DELAY_MS))
    }
  }
  throw lastErr ?? new Error('Mount failed')
}

/**
 * Mount a widget bundle (tgz) in the Service Worker and return the virtual URL for the iframe.
 * Prefix is derived from urn so multiple widgets don't clash. Retries if SW is not yet controlling.
 * When Service Worker API is unavailable (e.g. Android WebView on HTTP), throws so callers can use index.html fallback.
 */
export async function mountWidgetBundle(urn: string, tgzBlob: Blob, origin: string): Promise<string> {
  if (!hasServiceWorkerAPI()) {
    throw new Error('Service worker not available')
  }
  const pathId = pathIdForUrn(urn)
  const prefix = `${WIDGET_PATH_PREFIX}${pathId}/`
  const files = await extractTgzToFileMap(tgzBlob, prefix)
  await sendFilesToSwWithRetry(prefix, files)
  await new Promise(r => setTimeout(r, 50))
  const indexKey = Object.keys(files).find(k => k.endsWith('/index.html') && !k.includes('/._')) ?? `${prefix}index.html`
  return `${origin}${indexKey}`
}

/**
 * Extract index.html from a widget .tgz blob as a fallback when SW mount fails.
 * Returns a Blob for the HTML or null if not found / invalid.
 */
export async function extractWidgetIndexHtmlBlob(tgzBlob: Blob): Promise<Blob | null> {
  try {
    const buffer = new Uint8Array(await tgzBlob.arrayBuffer())
    const decompressed = decompressSync(buffer)
    const files = parseTar(decompressed)
    const indexKey = Object.keys(files).find(
      p => (p === 'index.html' || p.endsWith('/index.html')) && !p.includes('._')
    )
    const bytes = indexKey ? files[indexKey] : undefined
    if (!bytes) {
      return null
    }
    const copy = new Uint8Array(bytes.length)
    copy.set(bytes)
    return new Blob([copy], { type: 'text/html' })
  }
  catch {
    return null
  }
}

/**
 * Unmount a widget bundle (clear its files from the SW).
 */
export function unmountWidgetBundle(urn: string): void {
  if (!hasServiceWorkerAPI()) { return }
  const pathId = pathIdForUrn(urn)
  const prefix = `${WIDGET_PATH_PREFIX}${pathId}/`
  const sw = navigator.serviceWorker.controller
  if (sw) {
    sw.postMessage({ type: 'CLEAR_FILES', payload: { prefix } }, [])
  }
}

const CLEAR_CACHES_REPLY_TIMEOUT_MS = 5000

/**
 * Ask the widget Service Worker to clear its URL caches (runtime, data, Workbox precache) and
 * externalWidgetOrigins. Resolves when CLEAR_DONE is received or after timeout. No-op if SW unavailable.
 */
export function clearWidgetSwCaches(): Promise<void> {
  if (!hasServiceWorkerAPI()) {
    return Promise.resolve()
  }
  const registration = getWidgetServiceWorkerRegistration()
  const sw = registration?.active ?? navigator.serviceWorker?.controller
  if (!sw) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const timeout = setTimeout(() => {
      channel.port1.onmessage = () => {}
      resolve()
    }, CLEAR_CACHES_REPLY_TIMEOUT_MS)
    channel.port1.onmessage = (e: MessageEvent) => {
      if ((e.data as { type?: string })?.type === 'CLEAR_DONE') {
        clearTimeout(timeout)
        resolve()
      }
    }
    sw.postMessage({ type: 'CLEAR_CACHES' }, [channel.port2])
  })
}

const SW_UPDATE_FOR_WEB_OTA_TIMEOUT_MS = 8000

async function resolveWidgetServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = getWidgetServiceWorkerRegistration()
  if (existing) {
    return existing
  }
  if (!hasServiceWorkerAPI()) {
    return null
  }
  try {
    return await navigator.serviceWorker.getRegistration() ?? null
  }
  catch {
    return null
  }
}

/**
 * Before a browser Web OTA hard reload: check for a new `sw-widget.js`, wait for controller
 * activation, clear SW caches (including Workbox precache). On the second attempt, unregister
 * workers (escalation when soft clear + reload was not enough).
 */
export async function prepareBrowserWebOtaReload(options: { escalateUnregister?: boolean } = {}): Promise<void> {
  const { escalateUnregister = false } = options
  if (!hasServiceWorkerAPI()) {
    return
  }
  const registration = await resolveWidgetServiceWorkerRegistration()
  if (registration) {
    try {
      await registration.update()
    }
    catch {
      // still clear caches and reload
    }
    if (registration.waiting || registration.installing) {
      await waitForController(SW_UPDATE_FOR_WEB_OTA_TIMEOUT_MS)
    }
  }
  await clearWidgetSwCaches()
  if (escalateUnregister) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister()))
    }
    catch {
      // still reload
    }
  }
}

/**
 * Clears widget SW runtime caches and unregisters all workers for this origin.
 * Use **Tauri** (Android/desktop) for every web OTA apply, or **browser** as the second Web OTA
 * reload attempt when {@link prepareBrowserWebOtaReload} with `escalateUnregister` was not enough.
 */
export async function resetServiceWorkerForNewWebBundle(): Promise<void> {
  await clearWidgetSwCaches()
  if (!('serviceWorker' in navigator)) {
    return
  }
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    await Promise.all(regs.map(r => r.unregister()))
  }
  catch {
    // still reload — cache clear alone often fixes stale SWR responses
  }
}

const WIDGET_GZIP_TYPES = ['application/gzip', 'application/x-gzip']
const GZIP_MAGIC = new Uint8Array([0x1F, 0x8B])

/** Re-export for callers that only import from widgetSpaLoader. */
export { isAndroidWebView, useAndroidHtmlFallback } from '@/helpers/platform'

/** Above this size (bytes), use srcdoc instead of data URL to avoid duplicating large HTML in memory. */
export const SRCDOC_SIZE_THRESHOLD_BYTES = 100 * 1024

export function shouldUseSrcdocForHtmlBlob(blob: Blob): boolean {
  return blob.size > SRCDOC_SIZE_THRESHOLD_BYTES
}

/**
 * Convert an HTML Blob to a data URL (base64). Use as iframe src on Android instead of blob URL
 * when the platform doesn't handle blob: reliably.
 */
export function htmlBlobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}

/**
 * Convert an HTML Blob to a string for iframe srcdoc. Use for large widgets to avoid
 * duplicating content in a data URL.
 */
export async function htmlBlobToSrcdoc(blob: Blob): Promise<string> {
  return blob.text()
}

export function isWidgetTgzBlob(blob: Blob): boolean {
  return WIDGET_GZIP_TYPES.includes(blob.type)
}

/** Use when blob.type may be missing (e.g. from IDB or API). */
export async function isWidgetTgzBlobAsync(blob: Blob): Promise<boolean> {
  if (WIDGET_GZIP_TYPES.includes(blob.type)) {
    return true
  }
  if (blob.size < 2) {
    return false
  }
  const buf = await blob.slice(0, 2).arrayBuffer()
  const bytes = new Uint8Array(buf)
  return bytes[0] === GZIP_MAGIC[0] && bytes[1] === GZIP_MAGIC[1]
}

/**
 * Parse package.json from a widget .tgz blob. Used to read lucimark.singletonCapable.
 */
export async function getWidgetPackageJsonFromBlob(tgzBlob: Blob): Promise<TWidgetPackageJson | null> {
  try {
    const buffer = new Uint8Array(await tgzBlob.arrayBuffer())
    const decompressed = decompressSync(buffer)
    const files = parseTar(decompressed)
    return getWidgetPackageJsonFromTarFiles(files)
  }
  catch {
    return null
  }
}
